/** First-purchase products associated with the value customers bring later. */

import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { usePeriod } from "../lib/period.js";
import { Card, CardHead, moneyFmt } from "../components/cards.js";
import { RevenueCmToggle, useStickyRevenueCmMode } from "../components/RevenueCmToggle.js";
import { useCmReady } from "../lib/use-cm-ready.js";
import { PageLoadError } from "../components/PageLoadError.js";
import { pageLoadState } from "../lib/page-load-state.js";

interface LiftRow { product: string; buyers: number; avgFutureValue: number; lift: number }
interface LiftResp { overallAvgFutureValue: number; horizonDays: number; rows: LiftRow[] }

export function GatewayProductsCard({ revenueOnly = false }: { revenueOnly?: boolean } = {}): React.ReactElement {
  const api = useApiClient();
  const hasCm = useCmReady();
  const [mode, setMode] = useStickyRevenueCmMode("dd-mode-gateway-products");
  const cm = !revenueOnly && mode === "profit" && hasCm;
  const liftQ = useQuery({
    queryKey: ["product-ltv-lift", cm ? "cm" : "revenue"],
    queryFn: () => api.get<LiftResp>("/dashboard/product-ltv-lift", { mode: cm ? "cm" : "revenue" }),
  });
  const { data, isLoading } = liftQ;
  const requiredLoad = pageLoadState(liftQ);
  if (requiredLoad.failed) {
    return (
      <PageLoadError
        title="We couldn't load your customer LTV numbers"
        retrying={requiredLoad.retrying}
        onRetry={requiredLoad.retry}
      />
    );
  }
  const rows = data?.rows ?? [];
  const maxLift = Math.max(1.2, ...rows.map((r) => r.lift));
  const explain = revenueOnly
    ? [
        "<b>What it is:</b> Which products in a customer's first order are linked to more value afterward, measured on revenue before costs.",
        "<b>How it's calculated:</b> Group customers by the paid products in their first order. Exclude that first order, add the revenue they bring over the next 365 days, then compare each group with all customers who have a full 365 days of history.",
        "<b>Example:</b> A product at 1.50× means customers who started with it brought 50% more revenue afterward than average.",
        "<b>Note:</b> This is an association, not proof that the product caused the higher value. Each row needs at least 20 mature customers. Post-checkout upsells do not count as first purchases.",
      ]
    : [
        "<b>What it is:</b> Which products in a customer's first order are linked to more value afterward, measured on revenue or contribution margin (CM).",
        "<b>How it's calculated:</b> Group customers by the paid products in their first order. Exclude that first order, add the revenue or CM they bring over the next 365 days, then compare each group with all customers who have a full 365 days of history.",
        "<b>Example:</b> A product at 1.50× means customers who started with it brought 50% more value afterward than average.",
        "<b>Note:</b> This is an association, not proof that the product caused the higher value. Each row needs at least 20 mature customers. CM turns on after every selling product has costs.",
      ];

  return (
    <Card style={{ marginBottom: 0 }}>
      <CardHead title="Which first purchases lead to your most valuable customers?" explain={explain}>{revenueOnly ? null : <RevenueCmToggle mode={cm ? "profit" : "revenue"} setMode={setMode} cmDisabled={!hasCm} />}</CardHead>
      {isLoading ? (
        <div style={{ marginTop: 12, color: "var(--body)", fontSize: 12.5 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ marginTop: 12, color: "var(--body)", fontSize: 12.5, lineHeight: 1.5 }}>Not enough mature customers yet. This fills in when a first-purchase product has at least 20 customers with a full year of history.</div>
      ) : (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 9 }}>
          {rows.map((r) => {
            const good = r.lift >= 1.1;
            const bad = r.lift <= 0.9;
            const color = good ? "var(--good)" : bad ? "var(--bad)" : "var(--num)";
            return (
              <div key={r.product} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 150, fontSize: 12.5, color: "var(--text)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.product}>{r.product}</span>
                <div style={{ flex: 1, height: 16, background: "var(--surface-2)", borderRadius: 4, position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${Math.min(100, (r.lift / maxLift) * 100)}%`, background: good ? "var(--good)" : bad ? "var(--bad)" : "var(--accent)", opacity: 0.35, borderRadius: 4 }} />
                </div>
                <span className="dd-num" style={{ minWidth: 48, textAlign: "right", fontWeight: 700, color, fontSize: 14 }}>{r.lift.toFixed(2)}×</span>
                <span style={{ minWidth: 150, textAlign: "right", color: "var(--body-faint)", fontSize: 11.5 }}>{moneyFmt(r.avgFutureValue)} later · {r.buyers.toLocaleString()} customers</span>
              </div>
            );
          })}
          {data && (
            <div style={{ marginTop: 4, color: "var(--body-faint)", fontSize: 11.5 }}>Compared with the average customer's {data.horizonDays}-day {cm ? "CM" : "revenue"} after the first order: {moneyFmt(data.overallAvgFutureValue)}. Above 1× means better than average.</div>
          )}
        </div>
      )}
    </Card>
  );
}

interface Pair { a: string; b: string; orders: number }

/** Frequently bought together — top product pairs in the same order. */
export function BoughtTogetherCard(): React.ReactElement {
  const api = useApiClient();
  const period = usePeriod();
  const affinityQ = useQuery({
    queryKey: ["product-affinity", period.cacheKey],
    queryFn: () => api.get<{ pairs: Pair[] }>("/dashboard/product-affinity", { ...period.query }),
  });
  const { data, isLoading } = affinityQ;
  const requiredLoad = pageLoadState(affinityQ);
  if (requiredLoad.failed) {
    return (
      <PageLoadError
        title="We couldn't load your products"
        retrying={requiredLoad.retrying}
        onRetry={requiredLoad.retry}
      />
    );
  }
  const pairs = data?.pairs ?? [];
  return (
    <Card style={{ marginBottom: 0 }}>
      <CardHead title={`Frequently bought together · ${period.label}`} explain={[
        "<b>What it is:</b> The product pairs that show up in the same order most often, your natural bundles and cross-sell opportunities.",
        "<b>How it's calculated:</b> Across every order in the window you've picked, count how many contained both products, and rank the pairs. Free bonus items are excluded so they don't crowd the list.",
        "<b>Example (sample data · year-to-date):</b> Daily Greens + The Wellness Reset appear together on 1,428 orders, the strongest pairing, followed by Deep Sleep + The Wellness Reset (1,172).",
        "<b>Note:</b> Follows the date picker, so recent pairings and newer products show up instead of being buried by years of history. One caveat: a short window can miss a strong seasonal combo that sits just outside it, so widen the range when you're checking seasonal bundles. Use it to build bundles, set up cross-sells, or place products together.",
      ]} />
      {isLoading ? (
        <div style={{ marginTop: 12, color: "var(--body)", fontSize: 12.5 }}>Loading…</div>
      ) : pairs.length === 0 ? (
        <div style={{ marginTop: 12, color: "var(--body)", fontSize: 12.5, lineHeight: 1.5 }}>No multi-product orders yet — this fills in once customers buy more than one product per order.</div>
      ) : (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 7 }}>
          {pairs.map((p) => (
            <div key={`${p.a}|${p.b}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
              <span style={{ fontSize: 12.5, color: "var(--text)" }}>
                <span style={{ fontWeight: 600 }}>{p.a}</span>
                <span style={{ color: "var(--body-faint)", margin: "0 6px" }}>+</span>
                <span style={{ fontWeight: 600 }}>{p.b}</span>
              </span>
              <span className="dd-num" style={{ color: "var(--num)", fontSize: 12.5, whiteSpace: "nowrap" }}>{p.orders.toLocaleString()} orders</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

interface MoverRow { key: string; value: number; prev?: number }

/** Product movers — biggest revenue risers and faders vs the prior period. */
export function ProductMoversCard(): React.ReactElement {
  const api = useApiClient();
  const period = usePeriod();
  const hasCm = useCmReady();
  const [mode, setMode] = useStickyRevenueCmMode("dd-mode-product-movers");
  const cm = mode === "profit" && hasCm;
  // Stay on the Products page's item-level basis. The old generic revenue/CM
  // metrics netted refunds here even though the table, concentration card, and
  // monthly trend all stay before refunds, so the same product showed two
  // different dollar totals on one page.
  const metric = cm ? "product_cm" : "product_revenue";
  const enabled = period.key !== "all";
  const moversQ = useQuery({
    queryKey: ["product-movers", period.cacheKey, metric],
    enabled,
    queryFn: () => api.get<{ rows: MoverRow[] }>("/dashboard/breakdown", { metric, dimension: "product", ...period.query, compare: "prior" }),
  });
  const { data, isLoading, isPlaceholderData } = moversQ;
  const requiredLoad = pageLoadState(moversQ);
  if (requiredLoad.failed) {
    return (
      <PageLoadError
        title="We couldn't load your products"
        retrying={requiredLoad.retrying}
        onRetry={requiredLoad.retry}
      />
    );
  }
  const scored = (data?.rows ?? [])
    .map((r) => ({ key: r.key, value: r.value, prev: r.prev ?? 0, chg: r.value - (r.prev ?? 0), pct: (r.prev ?? 0) > 0 ? (r.value - (r.prev ?? 0)) / (r.prev ?? 1) : null }))
    .filter((r) => r.value > 0 || r.prev > 0);
  const risers = scored.filter((r) => r.chg > 0).sort((a, b) => (b.pct ?? 0) - (a.pct ?? 0)).slice(0, 4);
  const faders = scored.filter((r) => r.chg < 0).sort((a, b) => (a.pct ?? 0) - (b.pct ?? 0)).slice(0, 4);
  const pctLabel = (p: number | null): string => (p === null ? "new" : `${p >= 0 ? "▲" : "▼"} ${Math.abs(p * 100).toFixed(0)}%`);

  const Row = ({ r, up }: { r: (typeof scored)[number]; up: boolean }): React.ReactElement => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
      {/* flex:1 + minWidth:0 lets the name actually shrink so the ellipsis kicks
          in — without minWidth:0 a flex item won't shrink below its text and the
          long product name spills out of the narrow two-column box (founder). */}
      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: "var(--text)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.key}>{r.key}</span>
      <span className="dd-num" style={{ flexShrink: 0, color: up ? "var(--good)" : "var(--bad)", fontSize: 12.5, fontWeight: 700, whiteSpace: "nowrap" }}>{pctLabel(r.pct)} <span style={{ color: "var(--body-faint)", fontWeight: 400 }}>{moneyFmt(r.value)}</span></span>
    </div>
  );

  return (
    <Card style={{ marginBottom: 0 }}>
      <CardHead title={`What's rising & fading · ${period.label}`} explain={[
        "<b>What it is:</b> Which products are growing or shrinking most in revenue or contribution margin (CM) compared with the period before.",
        "<b>How it's calculated:</b> Each product's revenue or CM is compared with the same number in the period just before. It uses the same product dollars as the Product details table.",
        "<b>Example:</b> A product up 40% vs the prior period is your rising star; one down 30% is worth a look before it fades further.",
        "<b>Note:</b> Pick a set date range so there is a period to compare. Product revenue leaves out shipping and tax and stays before refunds. CM turns on after every selling product has costs.",
      ]}><RevenueCmToggle mode={cm ? "profit" : "revenue"} setMode={setMode} cmDisabled={!hasCm} /></CardHead>
      {!enabled ? (
        <div style={{ marginTop: 12, color: "var(--body)", fontSize: 12.5, lineHeight: 1.5 }}>Pick a date range at the top (30 days, 90 days, this year…) to see which products are rising and fading.</div>
      ) : isLoading || isPlaceholderData ? (
        <div role="status" style={{ marginTop: 12, color: "var(--body)", fontSize: 12.5 }}>Loading…</div>
      ) : risers.length === 0 && faders.length === 0 ? (
        <div style={{ marginTop: 12, color: "var(--body)", fontSize: 12.5 }}>Not enough change to show movers for this range.</div>
      ) : (
        <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--good)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}>Rising</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{risers.length ? risers.map((r) => <Row key={r.key} r={r} up />) : <span style={{ color: "var(--body-faint)", fontSize: 12 }}>—</span>}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--bad)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}>Fading</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{faders.length ? faders.map((r) => <Row key={r.key} r={r} up={false} />) : <span style={{ color: "var(--body-faint)", fontSize: 12 }}>—</span>}</div>
          </div>
        </div>
      )}
    </Card>
  );
}
